// PR-10.1 — pure helpers for league_rounds CRUD validation + status derivation.
// Kept in lib/ (not in component) so vitest can import without pulling in the
// supabase client. Mirrors the createTeamValidation.ts pattern from UPPDRAG-23.

export interface LeagueRoundLite {
  id?: string;
  round_number: number;
  name?: string | null;
  start_date: string; // ISO YYYY-MM-DD
  end_date: string;   // ISO YYYY-MM-DD
  status?: "upcoming" | "active" | "completed";
}

/**
 * Validates a draft set of rounds for a single league. Returns null on
 * valid; otherwise a Swedish error string ready for toast/inline display.
 *
 * Rules:
 *   - At least one round
 *   - round_number values are unique and form a 1..N sequence (no gaps)
 *   - Each round has start_date <= end_date
 *   - No two rounds overlap in date range (start..end inclusive)
 *   - Optional: rounds in round_number order have non-decreasing
 *     start_date (admin can interleave but a sequential sort is the
 *     expected default).
 *
 * Used by the admin UI's "Save rounds" mutation to short-circuit before
 * hitting Supabase. The DB also has CHECK constraints (start_date <=
 * end_date, UNIQUE(league_id, round_number)) so this is belt+braces.
 */
export function validateRoundDates(rounds: LeagueRoundLite[]): string | null {
  if (rounds.length === 0) {
    return "Minst en omgång krävs";
  }

  // round_number sequence 1..N
  const numbers = rounds.map((r) => r.round_number).sort((a, b) => a - b);
  for (let i = 0; i < numbers.length; i++) {
    if (numbers[i] !== i + 1) {
      return `Omgångsnummer måste vara 1..${numbers.length} utan luckor (saknas: ${i + 1})`;
    }
  }

  // start <= end per round
  for (const r of rounds) {
    if (r.start_date > r.end_date) {
      return `Omgång ${r.round_number}: slutdatum kan inte vara före startdatum`;
    }
  }

  // No date overlap between any two rounds
  const sorted = [...rounds].sort((a, b) => a.start_date.localeCompare(b.start_date));
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    // prev ends inclusively on prev.end_date; cur starts on cur.start_date.
    // Overlap iff cur.start_date <= prev.end_date.
    if (cur.start_date <= prev.end_date) {
      return `Omgångar ${prev.round_number} och ${cur.round_number} överlappar (${prev.end_date} vs ${cur.start_date})`;
    }
  }

  return null;
}

/**
 * Derives the natural status of a round from its date range relative to
 * the given "today" reference date (ISO YYYY-MM-DD). The cron in PR-10.2
 * will use this to auto-flip stored status as time passes; the UI uses
 * it to render the natural status when the stored value is stale.
 *
 *   today < start_date  → 'upcoming'
 *   today in [start, end] → 'active'
 *   today > end_date    → 'completed'
 */
export function computeRoundStatus(
  round: Pick<LeagueRoundLite, "start_date" | "end_date">,
  today: string,
): "upcoming" | "active" | "completed" {
  if (today < round.start_date) return "upcoming";
  if (today > round.end_date) return "completed";
  return "active";
}

/**
 * Returns true if `round` is "open" right now — i.e., it's the active
 * round AND, if it has an opens_after_round_id, that prerequisite round
 * is already completed. Used by the player-facing view in PR-10.2.
 *
 * In PR-10.1 the chain prerequisite is unused (no callsites yet); the
 * helper is included now so the DB shape + client logic stay aligned.
 */
export function isRoundOpen(
  round: Pick<LeagueRoundLite, "start_date" | "end_date" | "status">,
  prerequisiteRound: Pick<LeagueRoundLite, "status"> | null,
  today: string,
): boolean {
  // If the chain prerequisite isn't completed, the round can't be open
  // regardless of date.
  if (prerequisiteRound && prerequisiteRound.status !== "completed") {
    return false;
  }
  // Otherwise honour the date window.
  return computeRoundStatus(round, today) === "active";
}

/**
 * PR-10.2 — Filters a sorted list of rounds for the player-facing view
 * based on the league's `rounds_visible_ahead` admin setting.
 *
 * Behavior:
 *   - rounds_visible_ahead == null  → show all rounds (no limit)
 *   - rounds_visible_ahead == 0     → show only the current/most-recent
 *     active round + every completed round (no future)
 *   - rounds_visible_ahead == N (>0) → show all completed + active +
 *     the next N upcoming rounds
 *
 * "Current" is determined by date window first, then chain semantics.
 * Completed rounds are always visible (history). The cap only applies
 * to upcoming rounds — admin setting "1" gives players one round of
 * heads-up to plan availability.
 *
 * Input order: callers should pass rounds sorted by round_number ASC.
 * Output preserves the input order for stable rendering.
 */
export function filterVisibleRounds<R extends Pick<LeagueRoundLite, "round_number" | "start_date" | "end_date" | "status">>(
  rounds: R[],
  roundsVisibleAhead: number | null | undefined,
): R[] {
  if (roundsVisibleAhead == null) return rounds;
  if (roundsVisibleAhead < 0) return rounds; // defensive: treat negatives as "no limit"

  let upcomingShown = 0;
  return rounds.filter((r) => {
    if (r.status === "completed") return true;
    if (r.status === "active") return true;
    // status === 'upcoming'
    if (upcomingShown < roundsVisibleAhead) {
      upcomingShown += 1;
      return true;
    }
    return false;
  });
}
